import type {
  IncidentReportCreateCaseBody,
  IncidentReportUpdateBody,
  IncidentReportWithCase
} from '@lmc/ts-common'
import { postJSON } from 'common/services/api'
import { showToast } from 'common/utils'
import { useCallback } from 'react'
import { useMutation, useQueryClient } from 'react-query'

type UseIncidentReportProps = {
  incidentReportId: string | undefined
  incidentLogId?: string
  onUpdateSuccess?: () => void
  onUploadFilesSuccess?: () => void
}

type DeleteMutationBody = {
  deletedIndexes: number[]
}

const useIncidentReport = ({
  incidentReportId,
  incidentLogId,
  onUpdateSuccess,
  onUploadFilesSuccess
}: UseIncidentReportProps) => {
  const queryClient = useQueryClient()

  const handleError = (err: unknown) => {
    showToast({
      title: err as string,
      type: 'error'
    })
  }

  const updateFunction = (values: any) =>
    postJSON({
      url: `list/incident-report/${incidentReportId}/update-case`,
      method: 'PUT',
      json: values
    })

  const updateIncidentQueries = useCallback(() => {
    queryClient.invalidateQueries(['incident-report', incidentLogId])
    queryClient.invalidateQueries(['incident-report', incidentReportId])
    queryClient.invalidateQueries(['logs', incidentLogId])
    queryClient.invalidateQueries(['list-incidents'])
    queryClient.invalidateQueries(['incident-actions'])
  }, [queryClient, incidentLogId, incidentReportId])

  const { mutateAsync: updateMutation } = useMutation<
    IncidentReportWithCase,
    unknown,
    IncidentReportUpdateBody
  >(['incident-report', incidentReportId], updateFunction, {
    onSuccess: () => {
      onUpdateSuccess?.()
      updateIncidentQueries()
    }
  })

  const createFunction = (values: Partial<IncidentReportCreateCaseBody>) =>
    postJSON({
      url: `list/incident-report/${incidentReportId}/create-case`,
      method: 'POST',
      json: values
    })

  const { mutateAsync: createMutation } = useMutation<
    IncidentReportWithCase,
    unknown,
    Partial<IncidentReportCreateCaseBody>
  >(['incident-report', incidentReportId], createFunction, {
    onSuccess: () => {
      onUpdateSuccess?.()
      updateIncidentQueries()
    }
  })

  const uploadIncidentReportFilesFunction = async (formData: FormData) =>
    postJSON({
      url: `list/incident-report/${incidentReportId}/upload-file`,
      method: 'PUT',
      contentType: 'multipart/form-data',
      body: formData
    })

  const deleteIncidentReportFilesFunction = async (body: DeleteMutationBody) =>
    postJSON({
      url: `list/incident-report/${incidentReportId}/delete-files`,
      method: 'DELETE',
      json: {
        deletedIndexes: body.deletedIndexes
      }
    })

  const { mutateAsync: uploadFiles } = useMutation<
    any,
    unknown,
    FormData,
    unknown
  >(['incident-report', incidentReportId], uploadIncidentReportFilesFunction, {
    onSuccess: () => {
      showToast({
        title: 'Files uploaded'
      })
      onUploadFilesSuccess?.()
      updateIncidentQueries()
    },
    onError: (err) => handleError(err)
  })

  const { mutateAsync: deleteFiles } = useMutation<
    any,
    unknown,
    DeleteMutationBody,
    unknown
  >(['incident-report', incidentReportId], deleteIncidentReportFilesFunction, {
    onSuccess: () => {
      showToast({
        title: 'Files Deleted'
      })
      updateIncidentQueries()
    },
    onError: (err) => handleError(err)
  })

  return {
    updateIncidentReportCase: updateMutation,
    createIncidentReportCase: createMutation,
    uploadIncidentReportFiles: uploadFiles,
    deleteIncidentReportFiles: deleteFiles
  }
}

export default useIncidentReport
